import { Dispatch, useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Action } from 'redux';

import { OrderSideEnum, OrderStatusEnum, TimeInForceEnum } from '../../lib/enums';
import { OrderTypeEnum } from '../../lib/enums/order-type.enum';
import lang from '../../lib/language';
import { ReportingCall } from '../../lib/models/reporting/types';
import { Order } from '../../lib/models/trading/types';
import { RootState } from '../../lib/store';
import {
  getAccountTradeHistory,
  getOpenPositions as getPositions,
  setHasOrderUpdate,
} from '../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData, OpenPosition , PositionType } from '../../lib/store/reporting/types';
import { getCurrentBuyingPower } from '../../lib/store/selectors';
import { modifyOrder as serviceModifyOrder } from '../../lib/store/trading/index';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import NBBOPriceCache from '../../lib/store-util/NBBOPriceCache';
import TradePriceCache from '../../lib/store-util/TradePriceCache';
import { formatCurrency, formatNumber } from '../../lib/util/DataHelpers';
import { isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';
import { isMarketClosed, isMarketOpen } from '../../lib/util/MarketDataHelpers';
import { uuidv4 } from '../../lib/util/tools';
import { checkSymbolPostionType } from '../../lib/util/trading-position-helpers';
import { getOrderTotalValue } from '../../lib/util/TradingValidationsHelper';

import ArrowBack from '../../assets/img/blue_icon-arrow-left.svg';
import { ClickableDiv, Error, MaintenanceNotice, NotEnoughBuyingPower } from '../../components';
import ConfirmOrderPopupMobile from '../../components/ConfirmOrderPopupMobile/ConfirmOrderPopupMobile';
import HighLowLimitPriceAlert from '../../components/HighLowLimitPriceAlert/HighLowLimitPriceAlert';
import LimitPriceInput from '../../components/OrderActions/components/LimitPriceInput';
import MarketPriceInfo from '../../components/OrderActions/components/MarketPriceInfo';
import { getValidationPrice } from '../../components/OrderActions/OrderHelpers';
import useValidateOrder from '../../components/OrderActions/useValidateOrder';
import SharesCalculator from '../../components/SharesCalculator/SharesCalculator';
import { useMaintenanceMode, useTradingButtonsDisabled } from '../../hooks';
import Button from '../../shared_components/Button/Button';

import styles from './Modify.module.scss';

type Props = ReduxProps;

function Modify(props: Props) {
  const {
    positions,
    buyingPow,
    getOrders,
    myStocksUpdate,
    getOpenPositions,
    modifyOrder,
    accountStatusData,
  } = props;

  const navigate = useNavigate();
  const isMaintenance = useMaintenanceMode();
  const location = useLocation();
  const receivedData = location.state;

  const {
    symbol,
    parentOrderId,
    orderSide,
    orderType,
    initialPrice,
    orderQuantity,
    origin,
  } = receivedData;

  const isBuyOrder = orderSide === OrderSideEnum.Buy;

  const { marketState } = MarketStateCache.use();
  const showMarketInfoIcon = isMarketOpen(marketState);
  const marketClosed = isMarketClosed(marketState);

  const [criticalError, setCriticalError] = useState<string>('');
  const [targetPriceError, setTargetPriceError] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(orderQuantity);
  const [targetPrice, setTargetPrice] = useState<number>(initialPrice);
  const [showBP, setShowBP] = useState<boolean>(true);
  const [openedEnoughBPModal, setOpenedEnoughBPModal] = useState<boolean>(false);
  const [visibleHighLowLimitPriceAlert, setVisibleHighLowLimitPriceAlert] = useState<boolean>(false);
  const [showReviewOrder, setShowReviewOrder] = useState<boolean>(false);

  const isButtonsDisabled = useTradingButtonsDisabled({ symbol, accountStatusData });

  const nbboData = NBBOPriceCache.use(symbol);
  const { askprice, bidprice } = nbboData;
  const { currentPrice: tradePrice = 0 } = TradePriceCache.use(symbol, 'CreateOrder');
  const validationPirce = getValidationPrice(
    marketClosed,
    askprice,
    bidprice,
    tradePrice,
    isBuyOrder,
  );
  const { errorMessage, controlCheck } = useValidateOrder(
    orderType,
    validationPirce ?? 0,
    targetPrice,
    isBuyOrder,
  );
  const positionType = checkSymbolPostionType(symbol, positions);
  const positionQuantity = positions.find(
    (position: OpenPosition) => position.symbol === symbol,
  )?.quantity;

  useEffect(() => {
    if (positionType) {
      if (
        (orderSide === OrderSideEnum.Buy && positionType === PositionType.Sell)
        || (orderSide === OrderSideEnum.Sell && positionType === PositionType.Buy)
      ) {
        setShowBP(false);
      } else {
        setShowBP(true);
      }
    } else {
      setShowBP(orderSide === OrderSideEnum.Buy);
    }
  }, [orderSide, positionType]);

  const totalValue = getOrderTotalValue(orderType, quantity, tradePrice, targetPrice);

  const isValidOrder = useCallback(
    (qty: number, buyingPower: number): boolean => {
      setTargetPriceError(errorMessage);

      const buyingPowerCheck = buyingPower > totalValue;

      if (showBP && controlCheck && !buyingPowerCheck) {
        setOpenedEnoughBPModal(true);
      }

      if (!showBP) {
        return orderQuantity >= qty && controlCheck;
      }

      return buyingPowerCheck && controlCheck;
    },
    [controlCheck, errorMessage, orderQuantity, showBP, totalValue],
  );

  const onModifyClicked = () => {
    setCriticalError('');
    setTargetPriceError('');

    if (isValidOrder(quantity, buyingPow)) {
      setShowReviewOrder(true);
    }
  };

  const onReviewOrderClicked = () => {
    const price = tradePrice ?? 0;
    if (isValidOrder(quantity, buyingPow)) {
      const orderObject: Order = {};

      if (orderType === OrderTypeEnum.Stop) {
        orderObject.stopPrice = targetPrice;
      }
      orderObject.clientOrderId = uuidv4();
      orderObject.symbol = symbol;
      orderObject.side = orderSide as OrderSideEnum;
      orderObject.orderQty = quantity;
      orderObject.ordType = orderType as OrderTypeEnum;
      orderObject.price = targetPrice;
      orderObject.originalClientOrderId = parentOrderId;
      orderObject.traceIdentifier = OrderStatusEnum.Modified;

      if (
        orderType === OrderTypeEnum.Limit
        && !visibleHighLowLimitPriceAlert
        && askprice
        && bidprice
      ) {
        if (
          (isBuyOrder && orderObject.price > askprice)
          || (!isBuyOrder && orderObject.price < bidprice)
        ) {
          setVisibleHighLowLimitPriceAlert(
            isBuyOrder ? orderObject.price > askprice : orderObject.price < bidprice,
          );

          return;
        }
      }
      modifyOrder(orderObject);
      myStocksUpdate(true);
      if (!marketClosed) {
        getOpenPositions();
      }
      getOrders({
        symbols_StringList: symbol,
        tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS,
        isPendingOrdersCall: true,
      });
      sessionStorage.setItem('showUseDetailsEndModal', 'true');
      navigate(
        {
          pathname: origin,
        },
        { state: { data: orderObject }, replace: true },
      );
    }
  };

  const calculateMaximumQuantity = () => {
    if (positionQuantity) {
      const isMaximum = (positionQuantity < 0 && isBuyOrder) || (positionQuantity > 0 && !isBuyOrder);
      return isMaximum ? Math.abs(positionQuantity) : undefined;
    }
    return undefined;
  };

  return (
    <>
      <ClickableDiv
        className={styles.backArrow}
        onClick={() => {
          navigate(
            {
              pathname: origin,
            },
            { replace: true },
          );
        }}
      >
        <img src={ArrowBack} alt="Back" />
      </ClickableDiv>
      <div className={styles.wrapper}>
        <h6
          className={styles.title}
        >
          {`${lang.commonModify()} ${symbol} ${orderSide} ${lang.commonTradingOrder()}`}
        </h6>
        <div className={styles.wrapperOrderContainer}>
          <div className={styles.wrapperOrderType}>
            <div>{lang.commonTradingOrderType()}</div>
            <div className={styles.rigthAligned}>{orderType}</div>
          </div>
          <div className={styles.wrapperNumberOfShares}>
            <div className={styles.numberOfShares}>
              <div>{lang.mobileTradingTicketNumberOfShares()}</div>
              {!showBP && (
                <p className={styles.numberOfSharesSubText}>
                  {isBuyOrder
                    ? lang.webTradingShortedStocks(formatNumber(calculateMaximumQuantity(), true))
                    : lang.webTradingAvailableShares(
                      formatNumber(calculateMaximumQuantity(), true),
                    )}
                </p>
              )}
            </div>
            <div className={styles.wrapperSharesField}>
              <SharesCalculator
                quantity={quantity}
                setQuantity={setQuantity}
                maxQuantity={calculateMaximumQuantity()}
              />
            </div>
          </div>
          <LimitPriceInput
            initialPrice={initialPrice}
            orderType={orderType}
            targetPriceError={targetPriceError}
            setTargetPrice={(value) => setTargetPrice(value)}
            setTargetPriceError={(value) => setTargetPriceError(value)}
          />
          <MarketPriceInfo
            askPrice={askprice}
            bidPrice={bidprice}
            isMyAccount
            symbol={symbol}
            tradePrice={tradePrice}
            isMarketOpen={showMarketInfoIcon}
          />
          {orderType !== OrderTypeEnum.Market && (
            <div className={styles.expiryDateContainer}>
              <div className={styles.expiryDateText}>
                {lang.headerMyAccountPendingOrderExpiryDate2()}
              </div>
              <div className={styles.rightAligned}>
                {TimeInForceEnum.EndOfDay}
              </div>
            </div>
          )}
          <div className={styles.horizontalLine} />
          <div className={styles.availableBuyingContainer}>
            <div>{lang.commonTradingOrderValue2()}</div>
            <div className={styles.rigthAligned}>{formatCurrency(totalValue)}</div>
          </div>
          {showBP && (
            <div className={styles.availableBuyingContainer}>
              <div>{lang.mobileOrderCreateBuyingPower()}</div>
              <div className={styles.rigthAligned}>{formatCurrency(buyingPow)}</div>
            </div>
          )}
          {criticalError.trim() !== '' && (
            <div className={styles.errorWrapper}>
              <Error hiddenIcon errorMessage={criticalError} />
            </div>
          )}
          {openedEnoughBPModal && (
            <NotEnoughBuyingPower closeModal={() => setOpenedEnoughBPModal(false)} />
          )}
          {visibleHighLowLimitPriceAlert && (
            <HighLowLimitPriceAlert
              isBuyOrder={isBuyOrder}
              processModal={onReviewOrderClicked}
              toggleModal={() => setVisibleHighLowLimitPriceAlert(false)}
            />
          )}
          {showReviewOrder && (
            <ConfirmOrderPopupMobile
              symbol={symbol}
              action={orderSide}
              orderType={orderType}
              sharescount={quantity}
              marketPrice={tradePrice}
              targetPrice={targetPrice}
              orderValue={totalValue}
              onCancel={setShowReviewOrder}
              onConfirm={onReviewOrderClicked}
            />
          )}
        </div>
        {isMaintenance ? (
          <div className={styles.submitButton}>
            <MaintenanceNotice />
          </div>
        ) : (
          <Button
            className={styles.submitButton}
            biggerFontSize
            disabled={quantity <= 0 || isButtonsDisabled}
            onClick={onModifyClicked}
          >
            {lang.commonModify()}
          </Button>
        )}
      </div>
    </>
  );
}

const mapStateProps = (state: RootState) => ({
  positions: state.reporting.openPositions,
  isOpenPositionsReady: isCallStatusReady(
    ReportingCall.getOpenPositions,
    state.reporting.statusByCall,
  ),
  isPendingOrdersReady: isCallStatusReady(
    ReportingCall.getAccountPendingOrders,
    state.reporting.statusByCall,
  ),
  buyingPow: getCurrentBuyingPower(state),
  accountStatusData: state.closeout.accountStatusData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  modifyOrder: (order: Order) => dispatch(serviceModifyOrder(order)),
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
  getOrders: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  getOpenPositions: (symbol?: string) => dispatch(getPositions({ symbol })),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Modify);
